// src/Pages/Admin/UsersPage.js
import React, { useEffect, useState, useContext } from "react";
import AdminLayout from "./adminLayout";
import { AuthContext } from "../../contextStore/AuthContext"; // adjust path if necessary
import { fetchUsers, blockUser, exportUsers } from "../../api/admin";

export default function UsersPage() {
  const { token: userToken } = useContext(AuthContext) || {};
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);

  // choose token to use for admin calls: prefer admin_token (separate admin login), else fallback to user token
  function getEffectiveToken() {
    try {
      const adminTokenRaw = localStorage.getItem("admin_token") || localStorage.getItem("adminToken");
      const adminToken = adminTokenRaw ? String(adminTokenRaw).trim() : null;
      if (adminToken && adminToken !== "null" && adminToken !== "undefined") return adminToken;
    } catch (e) {
      // ignore localStorage errors
    }
    return userToken || null;
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [page]);

  async function load() {
    setLoading(true);
    try {
      const effectiveToken = getEffectiveToken();
      const data = await fetchUsers({
        token: effectiveToken,
        page,
        limit,
        q: q || undefined,
        city: city || undefined,
      });

      // Many APIs wrap results in { ok:true, users:[], total: n } or directly return { users, total }
      if (data) {
        if (Array.isArray(data.users) || Array.isArray(data.data)) {
          // support different shapes
          const list = data.users || data.data || [];
          setUsers(list);
          setTotal(data.total || data.count || list.length || 0);
        } else if (Array.isArray(data)) {
          setUsers(data);
          setTotal(data.length);
        } else {
          // if wrapper object with users property
          setUsers(data.users || []);
          setTotal(data.total || 0);
        }
      } else {
        setUsers([]);
        setTotal(0);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
      alert("Failed to load users");
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function handleBlockToggle(u) {
    const confirmMsg = u.is_blocked ? "Unblock this user?" : "Block this user?";
    if (!window.confirm(confirmMsg)) return;
    try {
      const effectiveToken = getEffectiveToken();
      await blockUser({ token: effectiveToken, id: u.id, block: !u.is_blocked });
      load();
    } catch (err) {
      console.error(err);
      alert("Failed to update user");
    }
  }

  async function handleExport(fmt = "csv") {
    try {
      const effectiveToken = getEffectiveToken();
      const res = await exportUsers({ token: effectiveToken, format: fmt, q: q || undefined, city: city || undefined });
      const blob = res.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${Date.now()}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Export failed");
    }
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <input placeholder="Search name/email/phone" value={q} onChange={(e) => setQ(e.target.value)} />
        <input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <button
          onClick={() => {
            setPage(0);
            load();
          }}
        >
          Filter
        </button>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={() => handleExport("csv")}>Export CSV</button>
          <button onClick={() => handleExport("xlsx")} style={{ marginLeft: 8 }}>
            Export XLSX
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        {loading ? <div>Loading users…</div> : null}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: 8 }}>ID</th>
            <th style={{ textAlign: "left", padding: 8 }}>Name</th>
            <th style={{ textAlign: "left", padding: 8 }}>Email</th>
            <th style={{ textAlign: "left", padding: 8 }}>Phone</th>
            <th style={{ textAlign: "left", padding: 8 }}>City</th>
            <th style={{ textAlign: "left", padding: 8 }}>Role</th>
            <th style={{ textAlign: "left", padding: 8 }}>Blocked</th>
            <th style={{ textAlign: "left", padding: 8 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ padding: 12, textAlign: "center" }}>
                No users found
              </td>
            </tr>
          ) : (
            users.map((u) => (
              <tr key={u.id} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: 8 }}>{u.id}</td>
                <td style={{ padding: 8 }}>{u.name}</td>
                <td style={{ padding: 8 }}>{u.email}</td>
                <td style={{ padding: 8 }}>{u.phone}</td>
                <td style={{ padding: 8 }}>{u.city}</td>
                <td style={{ padding: 8 }}>{u.role}</td>
                <td style={{ padding: 8 }}>{u.is_blocked ? "Yes" : "No"}</td>
                <td style={{ padding: 8 }}>
                  <button onClick={() => handleBlockToggle(u)}>{u.is_blocked ? "Unblock" : "Block"}</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <div>
          Showing page {page + 1} / {Math.ceil(total / limit) || 1}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Prev
          </button>
          <button style={{ marginLeft: 8 }} disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
